import { useEffect, useState } from "react";
import {
  DEFAULT_SCALE,
  listScalePorts,
  readWeight,
  scaleStatus,
  setScaleConfig,
  type ScalePort,
  type ScaleStatus,
} from "../lib/scale";
import { checkBridge, printTest, type BridgeStatus } from "../lib/printer";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../store/auth";
import { ui } from "../lib/ui";

type Tab = "hardware" | "fiscal" | "empresa" | "usuarios";

interface Settings {
  printerPort: string;
  scaleModel: string;
  scalePort: string;
  scaleBaud: number;
  cuit: string;
  ptoVenta: string;
  afipCertPath: string;
  afipModo: "homologacion" | "produccion";
  empresaNombre: string;
  empresaDireccion: string;
  empresaTel: string;
}

const DEFAULTS: Settings = {
  printerPort: "LPT1",
  scaleModel: DEFAULT_SCALE.model,
  scalePort: DEFAULT_SCALE.port,
  scaleBaud: DEFAULT_SCALE.baud,
  cuit: "",
  ptoVenta: "0001",
  afipCertPath: "",
  afipModo: "homologacion",
  empresaNombre: "MAXIKIOSKO JL",
  empresaDireccion: "",
  empresaTel: "",
};

const STORAGE_KEY = "pos-jl-settings";

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return DEFAULTS;
}

export default function Configuracion() {
  const currentUser = useAuth((s) => s.user);
  const isAdmin = (currentUser?.rol || "").toUpperCase() === "ADMIN";
  const [tab, setTab] = useState<Tab>("hardware");

  const tabs: { id: Tab; label: string }[] = [
    { id: "hardware", label: "Hardware" },
    { id: "fiscal", label: "Datos fiscales" },
    { id: "empresa", label: "Empresa" },
    ...(isAdmin ? [{ id: "usuarios" as Tab, label: "Usuarios" }] : []),
  ];
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [lastWeight, setLastWeight] = useState<number | null>(null);

  useEffect(() => {
    if (!savedMsg) return;
    const t = setTimeout(() => setSavedMsg(null), 2000);
    return () => clearTimeout(t);
  }, [savedMsg]);

  const update = <K extends keyof Settings>(k: K, v: Settings[K]) =>
    setSettings((s) => ({ ...s, [k]: v }));

  const save = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setSavedMsg("Configuración guardada");
  };

  const testScale = async () => {
    setMsg(null);
    try {
      const kg = await readWeight();
      setLastWeight(kg);
    } catch (e: unknown) {
      setMsg(
        `Error balanza: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  };

  const [bridge, setBridge] = useState<BridgeStatus | null>(null);
  const [scaleStat, setScaleStat] = useState<ScaleStatus | null>(null);
  const [comPorts, setComPorts] = useState<ScalePort[]>([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    void checkBridge(true).then(setBridge);
    const id = setInterval(() => void checkBridge(true).then(setBridge), 15_000);
    return () => clearInterval(id);
  }, []);

  // Poll del estado de la balanza cada 2s (cuando el bridge está vivo)
  useEffect(() => {
    if (!bridge?.available) return;
    let cancel = false;
    const tick = async () => {
      const s = await scaleStatus();
      if (!cancel) setScaleStat(s);
    };
    void tick();
    const id = setInterval(tick, 2000);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, [bridge?.available]);

  const refreshComPorts = async () => {
    if (!bridge?.available) {
      setMsg("Bridge no disponible — arrancalo en la PC del kiosko primero");
      return;
    }
    const ports = await listScalePorts();
    setComPorts(ports);
    // Auto-elegir el primero con likely_scale=true si scalePort no está en la lista
    const found = ports.find((p) => p.device === settings.scalePort);
    if (!found) {
      const candidate = ports.find((p) => p.likely_scale) || ports[0];
      if (candidate) update("scalePort", candidate.device);
    }
  };

  const applyScaleConfig = async () => {
    setMsg(null);
    if (!bridge?.available) {
      setMsg("Bridge no disponible");
      return;
    }
    try {
      const s = await setScaleConfig({
        port: settings.scalePort,
        baudrate: settings.scaleBaud,
        model: settings.scaleModel,
      });
      setScaleStat(s);
      setMsg(
        s.connected
          ? `Balanza conectada en ${s.port}`
          : `Balanza configurada pero no responde — ${s.disconnect_reason || "ver puerto"}`
      );
    } catch (e: unknown) {
      setMsg(
        `Error guardando config balanza: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  };

  const downloadBridge = async () => {
    setMsg(null);
    setDownloading(true);
    try {
      const res = await api.get("/api/bridge/download", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pos-jl-bridge.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMsg("Descarga iniciada. Descomprimí y ejecutá install_service.ps1 como Administrador.");
    } catch (e: unknown) {
      setMsg(
        `Error descargando bridge: ${e instanceof Error ? e.message : String(e)}`
      );
    } finally {
      setDownloading(false);
    }
  };

  const testPrinter = async () => {
    setMsg(null);
    const r = await printTest();
    setMsg(r.ok ? "Impresión enviada al bridge" : `Error: ${r.error}`);
  };

  const refreshBridge = async () => {
    setMsg("Chequeando bridge...");
    const r = await checkBridge(true);
    setBridge(r);
    setMsg(r.available ? `Bridge OK (v${r.version})` : `Bridge no responde: ${r.error}`);
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Configuración</h1>
          <p className={ui.subtitle}>Hardware, AFIP y datos de empresa</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && (
            <span className="text-sm text-emerald-600 font-medium">
              {savedMsg}
            </span>
          )}
          <button className={ui.btnPrimary} onClick={save}>
            Guardar
          </button>
        </div>
      </header>

      <div className={ui.page}>
        <div className="flex gap-2 mb-4 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={
                tab === t.id
                  ? "bg-slate-900 text-white px-4 py-2 rounded-lg font-medium"
                  : "bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium"
              }
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "hardware" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            <div className={ui.cardPad}>
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Impresora</h2>
                <div
                  className={
                    "flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium " +
                    (bridge?.available
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200")
                  }
                >
                  <div
                    className={
                      "w-1.5 h-1.5 rounded-full " +
                      (bridge?.available ? "bg-emerald-500 animate-pulse" : "bg-amber-500")
                    }
                  />
                  {bridge?.available ? `Bridge v${bridge.version}` : "Sin bridge"}
                </div>
              </div>
              <div className="space-y-3">
                <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                  El POS web usa un mini servicio local (<code>print-bridge</code>) en{" "}
                  <code className="text-emerald-700">localhost:9101</code> para hablar con la
                  impresora física. Si está activo, las ventas imprimen directo.
                  Si no, el ticket sale como vista previa en pantalla.
                </div>
                {bridge?.available && bridge.printers && bridge.printers.length > 0 && (
                  <div>
                    <label className={ui.label}>Impresoras configuradas</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {bridge.printers.map((p) => (
                        <span
                          key={p}
                          className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-mono"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {!bridge?.available && (
                  <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    <div>
                      No detecto el bridge. Para activarlo, descargá el instalador y corrélo
                      como Administrador en la PC del kiosko.
                    </div>
                    <button
                      onClick={downloadBridge}
                      disabled={downloading}
                      className="w-full px-3 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-bold rounded-lg flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                      {downloading ? "Descargando..." : "Descargar instalador del bridge"}
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    className={ui.btnSecondary + " flex-1"}
                    onClick={refreshBridge}
                  >
                    Refrescar status
                  </button>
                  <button
                    className={ui.btnPrimary + " flex-1"}
                    onClick={testPrinter}
                    disabled={!bridge?.available}
                  >
                    Imprimir prueba
                  </button>
                </div>
                {bridge?.available && (
                  <button
                    onClick={downloadBridge}
                    disabled={downloading}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    {downloading ? "Descargando..." : "Descargar instalador del bridge"}
                  </button>
                )}
              </div>
            </div>

            <div className={ui.cardPad}>
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Balanza</h2>
                <div
                  className={
                    "flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium " +
                    (scaleStat?.connected && scaleStat.receiving_data
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : scaleStat?.connected
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-slate-50 text-slate-500 border border-slate-200")
                  }
                >
                  <div
                    className={
                      "w-1.5 h-1.5 rounded-full " +
                      (scaleStat?.connected && scaleStat.receiving_data
                        ? "bg-emerald-500 animate-pulse"
                        : scaleStat?.connected
                          ? "bg-amber-500"
                          : "bg-slate-400")
                    }
                  />
                  {scaleStat?.connected && scaleStat.receiving_data
                    ? "Conectada"
                    : scaleStat?.connected
                      ? "Sin datos"
                      : "Sin balanza"}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={ui.label}>Modelo</label>
                  <select
                    className={ui.input}
                    value={settings.scaleModel}
                    onChange={(e) => update("scaleModel", e.target.value)}
                  >
                    <option value="systel">Systel (protocolo ENQ)</option>
                    <option value="torrey">Torrey MFQ</option>
                    <option value="otro">Otro / texto plano</option>
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={ui.label + " mb-0"}>
                      Puerto USB (COM virtual)
                    </label>
                    <button
                      type="button"
                      onClick={refreshComPorts}
                      disabled={!bridge?.available}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold disabled:opacity-40"
                    >
                      Detectar
                    </button>
                  </div>
                  {comPorts.length > 0 ? (
                    <select
                      className={ui.input}
                      value={settings.scalePort}
                      onChange={(e) => update("scalePort", e.target.value)}
                    >
                      {comPorts.map((p) => (
                        <option key={p.device} value={p.device}>
                          {p.device} — {p.description || "—"}
                          {p.likely_scale ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={ui.input}
                      value={settings.scalePort}
                      onChange={(e) => update("scalePort", e.target.value)}
                      placeholder="COM1"
                    />
                  )}
                  <div className="text-[11px] text-slate-500 mt-1">
                    La balanza se enchufa por USB. Click en "Detectar" para listar los
                    puertos COM disponibles (★ = USB-Serial probable).
                  </div>
                </div>
                <div>
                  <label className={ui.label}>Baudios</label>
                  <select
                    className={ui.input}
                    value={settings.scaleBaud}
                    onChange={(e) =>
                      update("scaleBaud", Number(e.target.value) || 9600)
                    }
                  >
                    <option value={9600}>9600 (default)</option>
                    <option value={4800}>4800</option>
                    <option value={19200}>19200</option>
                    <option value={38400}>38400</option>
                    <option value={115200}>115200</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    className={ui.btnSecondary + " flex-1"}
                    onClick={applyScaleConfig}
                    disabled={!bridge?.available}
                  >
                    Aplicar config
                  </button>
                  <button
                    className={ui.btnPrimary + " flex-1"}
                    onClick={testScale}
                    disabled={!bridge?.available}
                  >
                    Leer peso
                  </button>
                </div>
                {(lastWeight != null || scaleStat?.weight != null) && (
                  <div className="text-center py-2 bg-slate-50 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-600 tabular-nums">
                      {(lastWeight ?? scaleStat?.weight ?? 0).toFixed(3)} kg
                    </div>
                    {scaleStat?.stable === false && (
                      <div className="text-[11px] text-amber-600 mt-1">
                        Peso inestable — esperá a que la balanza confirme
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {msg && (
              <div className="md:col-span-2">
                <div className={ui.error}>{msg}</div>
              </div>
            )}
          </div>
        )}

        {tab === "fiscal" && (
          <div className={ui.cardPad + " max-w-2xl"}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">AFIP</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={ui.label}>CUIT</label>
                <input
                  className={ui.input}
                  value={settings.cuit}
                  onChange={(e) => update("cuit", e.target.value)}
                  placeholder="20-12345678-9"
                />
              </div>
              <div>
                <label className={ui.label}>Punto de venta</label>
                <input
                  className={ui.input}
                  value={settings.ptoVenta}
                  onChange={(e) => update("ptoVenta", e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <label className={ui.label}>Ruta certificado AFIP</label>
                <input
                  className={ui.input}
                  value={settings.afipCertPath}
                  onChange={(e) => update("afipCertPath", e.target.value)}
                  placeholder="C:\afip\cert.pem"
                />
              </div>
              <div className="col-span-2">
                <label className={ui.label}>Modo</label>
                <div className="flex gap-2">
                  <button
                    className={
                      settings.afipModo === "homologacion"
                        ? "flex-1 bg-orange-500 text-white px-3 py-2 rounded-lg text-sm font-bold"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => update("afipModo", "homologacion")}
                  >
                    Homologación
                  </button>
                  <button
                    className={
                      settings.afipModo === "produccion"
                        ? "flex-1 bg-emerald-500 text-white px-3 py-2 rounded-lg text-sm font-bold"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => update("afipModo", "produccion")}
                  >
                    Producción
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "empresa" && (
          <div className={ui.cardPad + " max-w-2xl"}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">
              Datos de empresa
            </h2>
            <div className="space-y-3">
              <div>
                <label className={ui.label}>Nombre</label>
                <input
                  className={ui.input}
                  value={settings.empresaNombre}
                  onChange={(e) => update("empresaNombre", e.target.value)}
                />
              </div>
              <div>
                <label className={ui.label}>Dirección</label>
                <input
                  className={ui.input}
                  value={settings.empresaDireccion}
                  onChange={(e) => update("empresaDireccion", e.target.value)}
                />
              </div>
              <div>
                <label className={ui.label}>Teléfono</label>
                <input
                  className={ui.input}
                  value={settings.empresaTel}
                  onChange={(e) => update("empresaTel", e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {tab === "usuarios" && isAdmin && <UsuariosPanel />}
      </div>
    </div>
  );
}

interface UserItem {
  id: number;
  username: string;
  nombre: string;
  rol: string;
  activo: boolean;
  created_at: string | null;
}

function UsuariosPanel() {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data } = await api.get<UserItem[]>("/api/auth/users");
      return data;
    },
  });

  const [form, setForm] = useState({
    username: "",
    nombre: "",
    password: "",
    rol: "CAJERO",
  });
  const [formError, setFormError] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: async () => {
      await api.post("/api/auth/users", form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setForm({ username: "", nombre: "", password: "", rol: "CAJERO" });
      setFormError(null);
    },
    onError: (e: unknown) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Error creando usuario";
      setFormError(detail);
    },
  });

  const togglerActivo = useMutation({
    mutationFn: async ({ id, activo }: { id: number; activo: boolean }) => {
      await api.patch(`/api/auth/users/${id}`, { activo });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const cambiarRol = useMutation({
    mutationFn: async ({ id, rol }: { id: number; rol: string }) => {
      await api.patch(`/api/auth/users/${id}`, { rol });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const resetPwd = useMutation({
    mutationFn: async ({ id, password }: { id: number; password: string }) => {
      await api.patch(`/api/auth/users/${id}`, { password });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <div className="space-y-4 max-w-4xl">
      {error && (
        <div className={ui.error}>
          No se pueden cargar usuarios. ¿Tu sesión todavía es admin?
        </div>
      )}

      <div className={ui.cardPad}>
        <h2 className="text-lg font-bold text-slate-900 mb-3">
          Nuevo usuario
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={ui.label}>Nombre completo</label>
            <input
              className={ui.input}
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Juan Pérez"
            />
          </div>
          <div>
            <label className={ui.label}>Usuario (login)</label>
            <input
              className={ui.input}
              value={form.username}
              onChange={(e) =>
                setForm({ ...form, username: e.target.value.toLowerCase() })
              }
              placeholder="juan"
              autoComplete="off"
            />
          </div>
          <div>
            <label className={ui.label}>Contraseña</label>
            <input
              type="password"
              className={ui.input}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="mín 4 caracteres"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={ui.label}>Rol</label>
            <select
              className={ui.input}
              value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value })}
            >
              <option value="CAJERO">Cajero</option>
              <option value="SUPERVISOR">Supervisor</option>
              <option value="ADMIN">Administrador</option>
            </select>
          </div>
        </div>
        {formError && (
          <div className={ui.error + " mt-3"}>{formError}</div>
        )}
        <button
          className={ui.btnPrimary + " w-full md:w-auto mt-4"}
          disabled={
            crear.isPending ||
            !form.username ||
            !form.nombre ||
            form.password.length < 4
          }
          onClick={() => crear.mutate()}
        >
          {crear.isPending ? "Creando..." : "Crear usuario"}
        </button>
      </div>

      <div className={ui.cardPad}>
        <h2 className="text-lg font-bold text-slate-900 mb-3">
          Usuarios existentes
        </h2>
        {isLoading ? (
          <div className={ui.skeleton + " h-32"} />
        ) : users.length === 0 ? (
          <p className={ui.empty}>Sin usuarios cargados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3">Usuario</th>
                  <th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">Rol</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="py-2 pr-3 font-mono text-slate-900">
                      {u.username}
                      {me?.id === u.id && (
                        <span className="ml-2 text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          VOS
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">{u.nombre}</td>
                    <td className="py-2 pr-3">
                      <select
                        className="text-xs font-bold rounded-md border border-slate-200 px-2 py-1 bg-white"
                        value={u.rol}
                        disabled={u.id === me?.id}
                        onChange={(e) =>
                          cambiarRol.mutate({ id: u.id, rol: e.target.value })
                        }
                      >
                        <option value="CAJERO">CAJERO</option>
                        <option value="SUPERVISOR">SUPERVISOR</option>
                        <option value="ADMIN">ADMIN</option>
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={
                          "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-bold " +
                          (u.activo
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500")
                        }
                      >
                        <span
                          className={
                            "w-1.5 h-1.5 rounded-full " +
                            (u.activo ? "bg-emerald-500" : "bg-slate-400")
                          }
                        />
                        {u.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right space-x-2">
                      <button
                        onClick={() => {
                          const pwd = window.prompt(
                            `Nueva contraseña para ${u.username}:`
                          );
                          if (pwd && pwd.length >= 4)
                            resetPwd.mutate({ id: u.id, password: pwd });
                          else if (pwd)
                            alert("Mín 4 caracteres");
                        }}
                        className="text-xs text-slate-600 hover:text-slate-900 font-semibold"
                      >
                        Reset pwd
                      </button>
                      <button
                        onClick={() =>
                          togglerActivo.mutate({
                            id: u.id,
                            activo: !u.activo,
                          })
                        }
                        disabled={u.id === me?.id}
                        className={
                          "text-xs font-semibold disabled:opacity-40 " +
                          (u.activo
                            ? "text-rose-600 hover:text-rose-700"
                            : "text-emerald-600 hover:text-emerald-700")
                        }
                      >
                        {u.activo ? "Desactivar" : "Activar"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
